import { createContext, useContext, useState, ReactNode, FC } from 'react'
import TransitionScreen from '@/ui/components/TransitionScreen/TransitionScreen'

interface TransitionScreenContextType {
    showTransitionScreen: boolean
    startTransitionScreen: (onComplete: () => void) => void
    setShowTransitionScreen: (visible: boolean) => void
    notifyPageReady: () => void
}

const TransitionScreenContext = createContext<TransitionScreenContextType | undefined>(undefined)

export const TransitionScreenProvider: FC<{ children: ReactNode; isDarkTheme?: boolean }> = ({
    children,
    isDarkTheme = false
}) => {
    const [isVisible, setIsVisible] = useState(false)
    const [callback, setCallback] = useState<(() => void) | null>(null)

    const startTransition = (onComplete: () => void) => {
        setIsVisible(true)
        setCallback(() => onComplete)
    }

    const handleAnimationComplete = () => {
        if (callback) {
            callback()
            setCallback(null)
        }
    }

    const contextValue = {
        showTransitionScreen: isVisible,
        startTransitionScreen: startTransition,
        setShowTransitionScreen: setIsVisible,
        notifyPageReady: () => { /* This can be used to trigger any actions when the page is ready, if needed */ }
    }

    return (
        <TransitionScreenContext.Provider value={contextValue}>
            {children}
            {isVisible && (
                <TransitionScreen
                    isVisible={isVisible}
                    onTransitionComplete={handleAnimationComplete}
                    duration={3000}
                    isDarkTheme={isDarkTheme}
                />
            )}
        </TransitionScreenContext.Provider>
    )
}

// the hook belongs to the context: the files that use it import both from here
// eslint-disable-next-line react-refresh/only-export-components
export const useTransitionScreen = () => {
    const context = useContext(TransitionScreenContext)
    if (!context) {
        throw new Error('useTransitionScreen must be used within TransitionScreenProvider')
    }
    return context
}
